// Operation completion is independent from the dispatch's remaining changes.
export const DEFERRED_SOURCE_CHANGES_HEADERS = Object.freeze({
  "X-TransIA-Source-Changes-Refresh": "deferred",
});

export const SOURCE_CHANGES_REFRESH_UNMOUNTED = Object.freeze({
  source_changes_refresh_unmounted: true,
});

export function isSourceChangesRefreshUnmounted(preview) {
  return preview?.source_changes_refresh_unmounted === true;
}

export function isSourceChangeFinalPreview(preview, scope) {
  if ((preview?.dispatch_date || preview?.dispatchDate) !== scope.dispatchDate
    || (preview?.dispatch_period || preview?.dispatchPeriod) !== scope.dispatchPeriod) return false;
  if (preview?.source_changes_unavailable === true) return scope.dispatchPeriod === "evening";
  return Boolean(preview?.counts && typeof preview.counts === "object" && !Array.isArray(preview.counts)
    && preview.details && typeof preview.details === "object" && !Array.isArray(preview.details)
    && (preview.preview_revision || preview.previewRevision)
    && preview.source_changes_refresh_required !== true
    && !(preview.metadata_sync?.order_ids || preview.metadataSync?.orderIds)?.length);
}

export function isAcceptableSourceChangeRefreshResult(preview, scope) {
  return isSourceChangesRefreshUnmounted(preview) || isSourceChangeFinalPreview(preview, scope);
}

export function sourceChangeMutationRefreshPeriods(payloads = [], fallbackPeriods = ["day", "evening"]) {
  if (!payloads.length) return fallbackPeriods;
  const hints = payloads.map((payload) => payload?.source_changes_refresh_periods);
  if (hints.some((periods) => !Array.isArray(periods) || !periods.length
    || periods.some((period) => !["day", "evening"].includes(period)))) return fallbackPeriods;
  return [...new Set(hints.flat())];
}

export function sourceChangeMutationScopes(dispatchDate, dispatchPeriod, payloads = [], periods = ["day", "evening"]) {
  const scopes = new Map();
  const add = (date, period) => {
    if (date && ["day", "evening"].includes(period)) {
      scopes.set(`${date}|${period}`, { dispatchDate: date, dispatchPeriod: period });
    }
  };
  add(dispatchDate, dispatchPeriod);
  periods.forEach((period) => add(dispatchDate, period));
  for (const payload of payloads) {
    const resolution = payload?.end_of_day_load_integrity_resolution || payload;
    add(resolution?.source_dispatch_date || payload?.source_dispatch_date, resolution?.source_dispatch_period || "day");
    add(resolution?.target_dispatch_date || payload?.target_dispatch_date, resolution?.target_period || payload?.target_period || dispatchPeriod);
  }
  return [...scopes.values()];
}

export function buildSourceChangeCompletionToast({ toast, payloads = [], refreshResult, captureStatus, expectedCounts = {}, onReload, displayRefreshRequired = false, applied = true }) {
  const counts = {};
  let replayFailed = false;
  let displayFailed = displayRefreshRequired || refreshResult?.previews?.some((preview) => preview?.result_refresh_required === true);
  for (const payload of payloads) {
    const mutation = payload?.source_changes || {};
    for (const [key, value] of Object.entries(mutation.mutation_counts || {})) {
      counts[key] = (counts[key] || 0) + Number(value || 0);
    }
    replayFailed ||= mutation.failed_truck_ids?.length > 0
      || payload?.route_refresh?.failed > 0
      || payload?.required_recalculation_failed === true;
    displayFailed ||= payload?.result_refresh_required === true;
  }
  const selectionIncomplete = Object.entries(expectedCounts).some(([key, count]) => (
    Number.isFinite(count) && count > 0 && Number(counts[key] || 0) < count
  ));
  const refreshFailed = refreshResult?.ok !== true;
  if (!applied) {
    return refreshFailed || displayFailed ? {
      ...toast, type: "warning",
      title: ["refused", "awaiting_confirmation"].includes(captureStatus) ? toast.title : "Action non confirmée — liste à recharger",
      message: `${toast.message} La liste n’a pas pu être actualisée. Rechargez la liste avant de réessayer.`,
      actionLabel: "Recharger la liste", onAction: onReload, failurePhase: displayFailed ? "display" : "refresh",
    } : toast;
  }
  if (refreshFailed || displayFailed || captureStatus === "partial" || replayFailed || selectionIncomplete) {
    const reason = refreshFailed || displayFailed
      ? `${toast.message || ""} La sauvegarde est enregistrée. Rechargez la liste sans relancer l’action.`.trim()
      : replayFailed
        ? "Les modifications sont enregistrées, mais le recalcul de certains camions a échoué."
        : selectionIncomplete
          ? "Les éléments traités sont enregistrés. Certains éléments sélectionnés restent à traiter."
          : toast.message || "Certains éléments restent à traiter.";
    return {
      ...toast, type: "warning", duration: 7000,
      title: refreshFailed || displayFailed ? "Action appliquée — liste à recharger" : "Action partiellement terminée",
      message: reason,
      actionLabel: "Recharger la liste", onAction: onReload,
      failurePhase: displayFailed ? "display" : refreshFailed ? "refresh" : "process",
    };
  }
  const hasRemaining = refreshResult.previews.some((preview) => (
    Number(preview?.counts?.unresolved_total ?? preview?.counts?.total ?? 0) > 0
    || Number(preview?.truck_changes?.counts?.unresolved ?? 0) > 0
    || preview?.source_change_readiness?.ready === false
  ));
  return {
    ...toast, type: "success",
    ...(hasRemaining ? {
      title: `${toast.title} — autres mises à jour en attente`,
      message: `${toast.message} D’autres mises à jour restent à traiter.`,
    } : {}),
  };
}
